import { Component } from '@theme/component';

/**
 * <quote-request> — the line-item quote builder behind `sections/quote-request.liquid`.
 *
 * Three jobs:
 *   1. Stock-code type-ahead. Fetches `sections/quote-search-results.liquid` via the
 *      predictive-search section render, because the JSON endpoint matches on SKU but
 *      does not return it (see `../notes/search-sku.md`). One request a keystroke,
 *      debounced, ~4 KB, and every row carries what a line needs — no follow-up call.
 *   2. The quote table. Lines are held in `#lines` and re-rendered; quantity and desired
 *      price are edited in place. A code we do not carry still adds a line, flagged
 *      `matched: false` — deliberate, see Decision 2 in `../plans/quote-page.md`.
 *   3. Submit. POSTs JSON to the `quote_request` Worker route with `lines` as an array.
 *
 * The in-progress quote is mirrored to localStorage: a specifier building a forty-line
 * schedule who refreshes should not start again.
 *
 * @typedef {Object} QuoteLine
 * @property {string} sku
 * @property {string} description
 * @property {string} variant_title
 * @property {number} qty
 * @property {string} desired_price
 * @property {boolean} matched
 * @property {string} url
 *
 * @typedef {Object} QuoteRequestRefs
 * @property {HTMLFormElement} form
 * @property {HTMLDivElement} formError
 * @property {HTMLSpanElement} formErrorText
 * @property {HTMLInputElement} searchInput
 * @property {HTMLInputElement} qtyInput
 * @property {HTMLButtonElement} addButton
 * @property {HTMLDivElement} results
 * @property {HTMLTableElement} table
 * @property {HTMLTableSectionElement} tableBody
 * @property {HTMLParagraphElement} empty
 * @property {HTMLDivElement} lineCount
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLDivElement} [success]
 * @property {HTMLDivElement} [liveRegion]
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const DEBOUNCE_MS = 250;
const MIN_TERM_LENGTH = 2;
const RESULTS_SECTION_ID = 'quote-search-results';

/** Keep the default field set — `fields` REPLACES it, it does not extend it. */
const SEARCH_FIELDS = 'title,product_type,variants.title,vendor,variants.sku';

/**
 * @extends {Component<QuoteRequestRefs>}
 */
class QuoteRequest extends Component {
  requiredRefs = [
    'form',
    'formError',
    'formErrorText',
    'searchInput',
    'qtyInput',
    'addButton',
    'results',
    'table',
    'tableBody',
    'empty',
    'lineCount',
    'submitButton',
  ];

  /** @type {QuoteLine[]} */
  #lines = [];
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #debounce;
  /** @type {AbortController | undefined} */
  #inFlight;
  /** Index of the keyboard-highlighted row, -1 for none. */
  #activeIndex = -1;

  connectedCallback() {
    super.connectedCallback();
    this.#restore();
    this.#renderLines();

    this.refs.form.addEventListener('submit', this.#handleSubmit);
    this.refs.searchInput.addEventListener('input', this.#handleSearchInput);
    this.refs.searchInput.addEventListener('keydown', this.#handleSearchKeydown);
    this.refs.addButton.addEventListener('click', this.#handleAddClick);
    this.refs.results.addEventListener('click', this.#handleResultsClick);
    this.refs.tableBody.addEventListener('input', this.#handleLineInput);
    this.refs.tableBody.addEventListener('focusout', this.#handleLineBlur);
    this.refs.tableBody.addEventListener('click', this.#handleLineClick);
    document.addEventListener('click', this.#handleDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#debounce);
    this.#inFlight?.abort();
    this.refs.form.removeEventListener('submit', this.#handleSubmit);
    this.refs.searchInput.removeEventListener('input', this.#handleSearchInput);
    this.refs.searchInput.removeEventListener('keydown', this.#handleSearchKeydown);
    this.refs.addButton.removeEventListener('click', this.#handleAddClick);
    this.refs.results.removeEventListener('click', this.#handleResultsClick);
    this.refs.tableBody.removeEventListener('input', this.#handleLineInput);
    this.refs.tableBody.removeEventListener('focusout', this.#handleLineBlur);
    this.refs.tableBody.removeEventListener('click', this.#handleLineClick);
    document.removeEventListener('click', this.#handleDocumentClick);
  }

  /** @param {string} key @param {string} fallback */
  #msg(key, fallback) {
    return this.dataset[key] || fallback;
  }

  #maxLines() {
    return Number(this.dataset.maxLines) || 60;
  }

  /* ---------------------------------------------------------------- storage */

  /**
   * localStorage can throw (private mode, blocked site data) and can come back empty.
   * Persistence is a convenience here, never a source of truth, so every path is
   * wrapped and a failure is silent.
   */
  #restore() {
    const key = this.dataset.storageKey;
    if (!key) return;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) this.#lines = parsed.filter(isQuoteLine).slice(0, this.#maxLines());
    } catch {
      /* no stored quote; carry on with an empty one */
    }
  }

  #persist() {
    const key = this.dataset.storageKey;
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify(this.#lines));
    } catch {
      /* storage unavailable or full — the quote still works for this visit */
    }
  }

  #clearStorage() {
    const key = this.dataset.storageKey;
    if (!key) return;
    try {
      localStorage.removeItem(key);
    } catch {
      /* nothing to clean up */
    }
  }

  /* ----------------------------------------------------------- type-ahead */

  #handleSearchInput = () => {
    clearTimeout(this.#debounce);
    const term = this.refs.searchInput.value.trim();
    if (term.length < MIN_TERM_LENGTH) {
      this.#closeResults();
      return;
    }
    this.#debounce = setTimeout(() => this.#search(term), DEBOUNCE_MS);
  };

  /**
   * Fetch the results section for a term.
   * @param {string} term
   * @param {AbortSignal} [signal]
   * @returns {Promise<Element | null>} the rendered results element, or null
   */
  async #fetchResults(term, signal) {
    const url = new URL('/search/suggest', window.location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[options][fields]', SEARCH_FIELDS);
    url.searchParams.set('section_id', RESULTS_SECTION_ID);

    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error(String(response.status));
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return doc.querySelector('[data-quote-results]');
  }

  /** @param {string} term */
  async #search(term) {
    this.#inFlight?.abort();
    const controller = new AbortController();
    this.#inFlight = controller;

    try {
      const rendered = await this.#fetchResults(term, controller.signal);
      // Only paint if the box still holds the term we searched for.
      if (this.refs.searchInput.value.trim() !== term) return;
      this.#openResults(rendered);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      // A failed lookup must not block the enquiry — they can still add the code by hand.
      this.#closeResults();
    }
  }

  /** @param {Element | null} rendered */
  #openResults(rendered) {
    this.refs.results.replaceChildren(...(rendered ? [rendered] : []));
    this.refs.results.hidden = !rendered;
    this.refs.searchInput.setAttribute('aria-expanded', rendered ? 'true' : 'false');
    this.#activeIndex = -1;
  }

  /**
   * Find the row whose SKU *is* the typed code.
   *
   * Someone who knows the code types it in full and hits Add without ever looking at the
   * dropdown — that must produce a real line, not an "we will look this up" one. Uses the
   * rows already on screen when they belong to this term, otherwise asks the server,
   * because Add can be pressed before the debounce has even fired.
   *
   * @param {string} term
   * @returns {Promise<HTMLElement | null>}
   */
  async #resolveExact(term) {
    const wanted = term.toLowerCase();
    /** @param {Element | null} scope */
    const exactIn = (scope) =>
      /** @type {HTMLElement | null} */ (
        Array.from(scope?.querySelectorAll('[data-quote-result]') ?? []).find(
          (row) => (row instanceof HTMLElement ? row.dataset.sku ?? '' : '').toLowerCase() === wanted
        ) ?? null
      );

    const onScreen = this.refs.results.querySelector('[data-quote-results]');
    if (onScreen instanceof HTMLElement && (onScreen.dataset.term ?? '').trim().toLowerCase() === wanted) {
      return exactIn(onScreen);
    }

    try {
      return exactIn(await this.#fetchResults(term));
    } catch {
      // Offline or the endpoint failed: fall back to an unmatched line rather than
      // refusing the enquiry.
      return null;
    }
  }

  #closeResults() {
    this.refs.results.replaceChildren();
    this.refs.results.hidden = true;
    this.refs.searchInput.setAttribute('aria-expanded', 'false');
    this.#activeIndex = -1;
  }

  /** @returns {HTMLButtonElement[]} */
  #resultRows() {
    return Array.from(this.refs.results.querySelectorAll('[data-quote-result]'));
  }

  /** @param {number} next */
  #highlight(next) {
    const rows = this.#resultRows();
    if (rows.length === 0) return;
    this.#activeIndex = (next + rows.length) % rows.length;
    rows.forEach((row, index) => {
      const active = index === this.#activeIndex;
      row.setAttribute('aria-selected', String(active));
      if (active) row.scrollIntoView({ block: 'nearest' });
    });
  }

  /** @param {KeyboardEvent} event */
  #handleSearchKeydown = (event) => {
    const rows = this.#resultRows();

    if (event.key === 'Escape') {
      this.#closeResults();
      return;
    }

    if (event.key === 'ArrowDown' && rows.length) {
      event.preventDefault();
      this.#highlight(this.#activeIndex + 1);
      return;
    }

    if (event.key === 'ArrowUp' && rows.length) {
      event.preventDefault();
      this.#highlight(this.#activeIndex - 1);
      return;
    }

    if (event.key === 'Enter') {
      // Never let Enter submit the whole quote from the search box.
      event.preventDefault();
      const active = rows[this.#activeIndex];
      if (active) {
        this.#addFromRow(active);
      } else {
        this.#addTypedCode();
      }
    }
  };

  /** @param {MouseEvent} event */
  #handleResultsClick = (event) => {
    const row = event.target instanceof Element ? event.target.closest('[data-quote-result]') : null;
    if (row instanceof HTMLElement) this.#addFromRow(row);
  };

  #handleDocumentClick = (event) => {
    if (this.refs.results.hidden) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#closeResults();
  };

  /* ---------------------------------------------------------------- lines */

  #qty() {
    const value = Number.parseInt(this.refs.qtyInput.value, 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /** @param {HTMLElement} row */
  #addFromRow(row) {
    this.#addLine({
      sku: row.dataset.sku || '',
      description: row.dataset.title || '',
      variant_title: row.dataset.variantTitle || '',
      qty: this.#qty(),
      desired_price: '',
      matched: true,
      url: row.dataset.url || '',
    });
  }

  /**
   * Add whatever is in the box. A code we carry becomes a proper line even though they
   * never touched the dropdown; anything else becomes an enquiry line.
   *
   * The unrecognised-code path is deliberate, not a fallback for failure: the old page
   * sold it in its own copy — those go to Frelan's dealer network — and it also covers
   * the 233 obsolete codes deleted in June and anything read off an old paper catalogue.
   */
  async #addTypedCode() {
    const typed = this.refs.searchInput.value.trim();
    if (!typed) return;

    const { addButton } = this.refs;
    addButton.disabled = true;
    try {
      const exact = await this.#resolveExact(typed);
      if (exact) {
        this.#addFromRow(exact);
        return;
      }
      this.#addLine({
        sku: typed,
        description: '',
        variant_title: '',
        qty: this.#qty(),
        desired_price: '',
        matched: false,
        url: '',
      });
    } finally {
      addButton.disabled = false;
    }
  }

  #handleAddClick = () => {
    const active = this.#resultRows()[this.#activeIndex];
    if (active) {
      this.#addFromRow(active);
    } else {
      void this.#addTypedCode();
    }
  };

  /** @param {QuoteLine} line */
  #addLine(line) {
    if (this.#lines.length >= this.#maxLines()) {
      this.#showFormError(this.#msg('msgMaxLines', 'You have reached the maximum number of lines.'));
      return;
    }

    // Same code twice is almost always a second thought about quantity, not a second
    // line. Matched on SKU alone: the same code added two different ways is still the
    // same product, and comparing `matched` too let JV850AB and a typed jv850ab sit on
    // the quote as separate lines.
    const existing = this.#lines.find(
      (candidate) => candidate.sku.toLowerCase() === line.sku.toLowerCase()
    );
    if (existing) {
      existing.qty += line.qty;
      // If the identified version arrives second, let it fill in what the bare code could
      // not — never the other way round.
      if (line.matched && !existing.matched) {
        existing.description = line.description;
        existing.variant_title = line.variant_title;
        existing.url = line.url;
        existing.sku = line.sku;
        existing.matched = true;
      }
      this.#announce(this.#msg('msgDuplicate', 'Quantity updated.'));
    } else {
      this.#lines.push(line);
      this.#announce(this.#msg('msgAdded', 'Added to your quote.'));
    }

    this.#clearFormBanners();
    this.#renderLines();
    this.#persist();

    this.refs.searchInput.value = '';
    this.refs.qtyInput.value = '1';
    this.#closeResults();
    this.refs.searchInput.focus();
  }

  /** @param {number} index */
  #removeLine(index) {
    this.#lines.splice(index, 1);
    this.#renderLines();
    this.#persist();
  }

  #renderLines() {
    const { tableBody, table, empty, lineCount } = this.refs;
    tableBody.replaceChildren();

    for (const [index, line] of this.#lines.entries()) {
      tableBody.append(this.#renderLine(line, index));
    }

    const has = this.#lines.length > 0;
    table.hidden = !has;
    empty.hidden = has;
    lineCount.textContent = has ? this.#countLabel(this.#lines.length) : '';
  }

  /** @param {number} count */
  #countLabel(count) {
    const template =
      count === 1
        ? this.dataset.labelCountOne || '{{ count }} line'
        : this.dataset.labelCountOther || '{{ count }} lines';
    return template.replace('{{ count }}', String(count));
  }

  /**
   * Built with DOM calls rather than innerHTML: `description` comes back from the search
   * response and `sku` can be anything the customer typed.
   * @param {QuoteLine} line
   * @param {number} index
   */
  #renderLine(line, index) {
    const row = document.createElement('tr');
    row.dataset.index = String(index);

    const skuCell = document.createElement('td');
    skuCell.className = 'quote-request__line-sku';
    skuCell.dataset.label = this.refs.table.tHead?.rows[0]?.cells[0]?.textContent?.trim() || '';
    skuCell.textContent = line.sku;
    row.append(skuCell);

    const descCell = document.createElement('td');
    descCell.dataset.label = this.refs.table.tHead?.rows[0]?.cells[1]?.textContent?.trim() || '';
    if (line.description) {
      const title = document.createElement('span');
      title.className = 'quote-request__line-title';
      title.textContent = line.description;
      descCell.append(title);
    }
    if (line.variant_title) {
      const variant = document.createElement('span');
      variant.className = 'quote-request__line-variant';
      variant.textContent = line.variant_title;
      descCell.append(variant);
    }
    if (!line.matched) {
      const flag = document.createElement('span');
      flag.className = 'quote-request__line-flag';
      flag.textContent = this.dataset.labelUnmatched || 'We will look this code up for you';
      descCell.append(flag);
    }
    row.append(descCell);

    const priceCell = document.createElement('td');
    priceCell.dataset.label = this.refs.table.tHead?.rows[0]?.cells[2]?.textContent?.trim() || '';
    // The £ is a static prefix, never part of the value: what travels to the Worker is
    // always a plain decimal, and there is no symbol for anyone to type, omit or
    // duplicate.
    const priceWrap = document.createElement('span');
    priceWrap.className = 'quote-request__price';
    const symbol = document.createElement('span');
    symbol.className = 'quote-request__price-symbol';
    symbol.setAttribute('aria-hidden', 'true');
    symbol.textContent = '£';
    const price = document.createElement('input');
    price.type = 'text';
    price.className = 'quote-request__line-input quote-request__price-input';
    price.value = line.desired_price;
    price.inputMode = 'decimal';
    price.autocomplete = 'off';
    price.placeholder = '0.00';
    price.dataset.lineField = 'desired_price';
    price.setAttribute('aria-label', `${this.refs.table.tHead?.rows[0]?.cells[2]?.textContent?.trim() || 'Price'} in pounds — ${line.sku}`);
    priceWrap.append(symbol, price);
    priceCell.append(priceWrap);
    row.append(priceCell);

    const qtyCell = document.createElement('td');
    qtyCell.dataset.label = this.refs.table.tHead?.rows[0]?.cells[3]?.textContent?.trim() || '';
    const qty = document.createElement('input');
    qty.type = 'number';
    qty.min = '1';
    qty.step = '1';
    qty.className = 'quote-request__line-input';
    qty.value = String(line.qty);
    qty.dataset.lineField = 'qty';
    qty.setAttribute('aria-label', `${this.refs.table.tHead?.rows[0]?.cells[3]?.textContent?.trim() || 'Quantity'} — ${line.sku}`);
    qtyCell.append(qty);
    row.append(qtyCell);

    const removeCell = document.createElement('td');
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'quote-request__button quote-request__line-remove';
    remove.dataset.lineRemove = '';
    remove.textContent = this.dataset.labelRemove || 'Remove';
    remove.setAttribute('aria-label', `${this.dataset.labelRemove || 'Remove'} ${line.sku}`);
    removeCell.append(remove);
    row.append(removeCell);

    return row;
  }

  /** @param {Event} event */
  #handleLineInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.lineField) return;
    const row = input.closest('tr');
    const index = Number(row?.dataset.index);
    const line = this.#lines[index];
    if (!line) return;

    if (input.dataset.lineField === 'qty') {
      const value = Number.parseInt(input.value, 10);
      line.qty = Number.isFinite(value) && value > 0 ? value : 1;
    } else {
      // Reject anything that is not part of a decimal as it is typed, rather than
      // complaining about it later. Only rewrite the box when something was actually
      // stripped, so the caret stays where it was for normal typing.
      const cleaned = cleanPrice(input.value);
      if (cleaned !== input.value) input.value = cleaned;
      line.desired_price = cleaned;
    }
    this.#persist();
  };

  /**
   * Settle the price to two decimals once they leave the field: 11 becomes 11.00, 11.5
   * becomes 11.50, a stray "." clears. Done on the way out rather than while typing,
   * which would fight them mid-entry.
   * @param {FocusEvent} event
   */
  #handleLineBlur = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.dataset.lineField !== 'desired_price') return;
    const row = input.closest('tr');
    const line = this.#lines[Number(row?.dataset.index)];
    if (!line) return;

    const settled = formatPrice(input.value);
    input.value = settled;
    line.desired_price = settled;
    this.#persist();
  };

  /** @param {MouseEvent} event */
  #handleLineClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-line-remove]') : null;
    if (!button) return;
    const row = button.closest('tr');
    const index = Number(row?.dataset.index);
    if (Number.isFinite(index)) this.#removeLine(index);
  };

  /* ------------------------------------------------------------ validation */

  /** @returns {HTMLElement[]} */
  #fields() {
    return Array.from(this.refs.form.querySelectorAll('[data-quote-field]'));
  }

  /** @param {HTMLElement} field */
  #errorContainerFor(field) {
    return field.id ? this.refs.form.querySelector(`[data-error-for="${field.id}"]`) : null;
  }

  /** @param {HTMLElement} field @param {string} message */
  #setFieldError(field, message) {
    field.setAttribute('aria-invalid', 'true');
    const container = this.#errorContainerFor(field);
    if (container instanceof HTMLElement) {
      container.classList.remove('hidden');
      const span = container.querySelector('span');
      if (span) span.textContent = message;
    }
  }

  #clearFieldErrors() {
    for (const field of this.#fields()) {
      field.removeAttribute('aria-invalid');
      const container = this.#errorContainerFor(field);
      if (container instanceof HTMLElement) {
        container.classList.add('hidden');
        const span = container.querySelector('span');
        if (span) span.textContent = '';
      }
    }
  }

  #clearFormBanners() {
    this.refs.formError.classList.add('hidden');
    this.refs.formErrorText.textContent = '';
  }

  /** Mirrors the Worker's own checks — faster feedback, no round trip. */
  #validate() {
    /** @type {HTMLElement | null} */
    let firstInvalid = null;
    const requiredMsg = this.#msg('msgRequired', 'This field is required.');
    const emailMsg = this.#msg('msgInvalidEmail', 'Please enter a valid email address.');

    for (const field of this.#fields()) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) continue;
      const value = String(field.value).trim();
      let problem = '';
      if (field.required && !value) {
        problem = requiredMsg;
      } else if (value && field.dataset.fieldType === 'email' && !EMAIL_RE.test(value)) {
        problem = emailMsg;
      }
      if (problem) {
        this.#setFieldError(field, problem);
        firstInvalid ??= field;
      }
    }

    if (firstInvalid) {
      firstInvalid.focus();
      firstInvalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return false;
    }

    if (this.#lines.length === 0) {
      this.#showFormError(this.#msg('msgNoLines', 'Add at least one product to your quote.'));
      this.refs.searchInput.focus();
      return false;
    }

    return true;
  }

  /* ---------------------------------------------------------------- submit */

  /** @param {string} message */
  #showFormError(message) {
    this.refs.formError.classList.remove('hidden');
    this.refs.formErrorText.textContent = message;
    this.refs.formError.focus();
    this.#announce(message);
  }

  /** @param {string} message */
  #announce(message) {
    if (this.refs.liveRegion) this.refs.liveRegion.textContent = message;
  }

  #showSuccess() {
    this.refs.form.hidden = true;
    if (this.refs.success) {
      this.refs.success.hidden = false;
      this.refs.success.focus();
      this.refs.success.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  /**
   * Built from our own fields, not from FormData over the whole form.
   *
   * Shopify injects its own hidden inputs into forms on preview and password-protected
   * URLs — `oseid` is the preview session — and sweeping up everything in the form sent
   * those to the inbox as if a customer had typed them. Naming what we collect keeps
   * anything Shopify, an app or a browser extension adds out of the payload for good.
   */
  #payload() {
    /** @type {Record<string, unknown>} */
    const payload = {
      form_key: 'quote_request',
      // Without the query string: preview and tracking parameters are noise in an inbox.
      page_url: window.location.origin + window.location.pathname,
      lines: this.#lines,
    };

    for (const field of this.#fields()) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) continue;
      if (!field.name) continue;
      // An unticked checkbox submits nothing; report it either way so the email is unambiguous.
      payload[field.name] = field.type === 'checkbox' ? String(field.checked) : field.value.trim();
    }

    const formData = new FormData(this.refs.form);
    payload.hp_field = String(formData.get('hp_field') ?? '');
    payload.turnstile_token = String(formData.get('cf-turnstile-response') ?? '');
    return payload;
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    event.preventDefault();
    this.#clearFormBanners();
    this.#clearFieldErrors();
    if (!this.#validate()) return;

    const endpointUrl = this.dataset.endpointUrl;
    if (!endpointUrl) {
      this.#showFormError(this.#msg('msgConfigError', 'This form is not configured yet.'));
      return;
    }

    const { submitButton } = this.refs;
    const originalLabel = submitButton.textContent;
    submitButton.disabled = true;
    submitButton.textContent = this.#msg('msgSubmitting', 'Sending…');

    try {
      const response = await fetch(endpointUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.#payload()),
      });
      const result = await response.json().catch(() => ({ ok: false }));
      if (response.ok && result.ok) {
        this.#lines = [];
        this.#clearStorage();
        this.refs.form.reset();
        this.#showSuccess();
      } else {
        this.#showFormError(result.error || this.#msg('msgError', 'Something went wrong. Please try again.'));
      }
    } catch {
      this.#showFormError(
        this.#msg('msgNetworkError', 'Could not reach the server. Please check your connection and try again.')
      );
    } finally {
      submitButton.disabled = false;
      submitButton.textContent = originalLabel;
    }
  };
}

/**
 * Digits and at most one decimal point. The £ is a static prefix in the markup, so a
 * symbol typed into the box is simply not part of a price.
 * @param {string} value
 */
function cleanPrice(value) {
  const digitsAndDots = value.replace(/[^0-9.]/g, '');
  const firstDot = digitsAndDots.indexOf('.');
  const cleaned =
    firstDot === -1
      ? digitsAndDots
      : digitsAndDots.slice(0, firstDot + 1) + digitsAndDots.slice(firstDot + 1).replace(/\./g, '');
  // Two decimals is a price; more is a typo.
  const [pounds = '', pence] = cleaned.split('.');
  const capped = pence === undefined ? pounds : `${pounds}.${pence.slice(0, 2)}`;
  return capped.slice(0, 12);
}

/**
 * "11" → "11.00", "11.5" → "11.50", "" or "." → "". Zero clears too: a line priced at
 * nothing is someone who changed their mind, not an offer of £0.00.
 * @param {string} value
 */
function formatPrice(value) {
  const cleaned = cleanPrice(value);
  if (!cleaned || cleaned === '.') return '';
  const amount = Number(cleaned);
  return Number.isFinite(amount) && amount > 0 ? amount.toFixed(2) : '';
}

/**
 * Stored quotes are replayed into the DOM, so anything shaped wrong is dropped rather
 * than trusted.
 * @param {unknown} value
 * @returns {value is QuoteLine}
 */
function isQuoteLine(value) {
  if (typeof value !== 'object' || value === null) return false;
  const line = /** @type {Record<string, unknown>} */ (value);
  return typeof line.sku === 'string' && typeof line.qty === 'number' && Number.isFinite(line.qty);
}

if (!customElements.get('quote-request')) {
  customElements.define('quote-request', QuoteRequest);
}
